import { Injectable, NgZone } from '@angular/core';
import {
  Auth,
  GoogleAuthProvider,
  User,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  onIdTokenChanged,
} from '@angular/fire/auth';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

// User roles
export const ROLES = [
  'god',
  'dev',
  'admin',
  'finance',
  'pm',
  'el',
  'ic',
] as const;
export type UserRole = (typeof ROLES)[number];

const EMAIL_DOMAIN = '@cannonballcreative.agency';

const ROLE_USERS: Record<UserRole, string[]> = {
  god: ['ziad', 'rhobbs'],
  dev: ['ericj'],
  admin: [
    'dixie',
    'andrew',
    'chris',
    'connor',
    'jlutz',
    'erinh',
    'mikeyg',
    'dfinnane',
    '',
  ],
  finance: ['pkruglik', 'finance', ''],
  pm: ['sdaneva', 'hunterh', 'mmurphy', ''],
  el: [
    'jakef',
    'jfrick',
    'sapetrie',
    'abella',
    'mhaverkamp',
    'chornbeck',
    'nsmith',
    'amcdonald',
    '',
  ],
  ic: [''],
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  user$: Observable<User | null>;
  // User roles
  // Hardcoded email-to-role mapping (auto-generated from ROLE_USERS)
  // Create a temporary mapping of emails to all their roles
  private buildEmailRoleMap(): Record<string, UserRole> {
    // First, build a map of emails to all their assigned roles
    const emailToRolesMap: Record<string, UserRole[]> = {};

    // Loop through each role and assign to emails
    Object.entries(ROLE_USERS).forEach(([role, users]) => {
      users.forEach((user) => {
        if (user) {
          // Skip empty strings
          let email: string;
          if (user.includes('@')) {
            // If user already contains @, use it as-is (for external emails like ziad@feralcreative.co)
            email = user;
          } else {
            // Otherwise, append the default domain
            email = `${user}${EMAIL_DOMAIN}`;
          }
          if (!emailToRolesMap[email]) {
            emailToRolesMap[email] = [];
          }
          emailToRolesMap[email].push(role as UserRole);
        }
      });
    });

    // Now build the final map choosing highest priority role for each user
    const finalMap: Record<string, UserRole> = {};

    Object.entries(emailToRolesMap).forEach(([email, roles]) => {
      // Find the highest priority role (earliest in ROLES array)
      let highestRole: UserRole | null = null;
      // Using number type as we're comparing indices
      let highestRoleIndex: number = ROLES.length;

      roles.forEach((role) => {
        const roleIndex = ROLES.indexOf(role);
        if (roleIndex >= 0 && roleIndex < highestRoleIndex) {
          highestRole = role;
          highestRoleIndex = roleIndex;
        }
      });

      if (highestRole) {
        finalMap[email] = highestRole;
      }
    });

    return finalMap;
  }

  // Map email addresses to their highest priority roles
  private emailRoleMap: Record<string, UserRole> = this.buildEmailRoleMap();

  // All Cannonball email addresses are allowed to access the portal
  // This checks if an email ends with the EMAIL_DOMAIN or is an explicitly allowed external email
  private isCannonballEmail(email: string): boolean {
    const lowerEmail = email.toLowerCase();
    return (
      lowerEmail.endsWith(EMAIL_DOMAIN.toLowerCase()) ||
      lowerEmail === 'ziad@feralcreative.co'
    );
  }

  // For access control check - all Cannonball emails are allowed
  private allowedEmails: string[] = [];

  // Initialize the allowed emails list
  private initAllowedEmails() {
    // Start with explicitly assigned users
    this.allowedEmails = Object.keys(this.emailRoleMap);
    // Note: We don't need to add specific IC emails here since the access
    // check will use isCannonballEmail for any email not in emailRoleMap
  }

  // Observable for the current user's role
  public userRole$!: Observable<UserRole | null>;

  // Get role for a given email
  public getUserRole(email: string | null | undefined): UserRole | null {
    if (!email) return null;

    // If email has an explicitly assigned role, return it
    if (this.emailRoleMap[email]) {
      return this.emailRoleMap[email];
    }

    // If it's a Cannonball email without explicit role, assign IC role by default
    if (this.isCannonballEmail(email)) {
      return 'ic';
    }

    // Non-Cannonball emails with no explicit role get null
    return null;
  }

  // Track the most recent auth error
  public authError: string | null = null;

  constructor(
    private auth: Auth,
    private router: Router,
    private ngZone: NgZone
  ) {
    // Initialize allowed emails list
    this.initAllowedEmails();
    // Initialize user$ observable from auth state
    // --- PATCH: Set __session cookie after login and on token refresh ---
    onAuthStateChanged(this.auth, async (user: User | null) => {
      if (user) {
        const token = await user.getIdToken();
        document.cookie = `__session=${token}; path=/; secure; samesite=strict`;
      } else {
        // Clear the cookie on logout
        document.cookie = `__session=; path=/; secure; samesite=strict; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      }
    });
    onIdTokenChanged(this.auth, async (user: User | null) => {
      if (user) {
        const token = await user.getIdToken(true); // force refresh
        document.cookie = `__session=${token}; path=/; secure; samesite=strict`;
      }
    });
    // --- END PATCH ---

    // Initialize user$ observable from auth state
    this.user$ = new Observable<User | null>((subscriber) => {
      console.log('Setting up auth state subscription');
      const unsubscribe = this.auth.onAuthStateChanged(
        (user) => {
          console.log('Auth state changed:', {
            loggedIn: !!user,
            email: user?.email,
            displayName: user?.displayName,
            photoURL: user?.photoURL,
          });
          subscriber.next(user);
        },
        (error) => {
          console.error('Auth state error:', error);
        }
      );
      return unsubscribe;
    }).pipe(
      tap((user) => {
        if (user?.photoURL) {
          // Pre-fetch the avatar image to ensure it's cached
          const img = new Image();
          img.src = user.photoURL;
        }
      })
    );

    // Set up userRole$ observable
    this.userRole$ = this.user$.pipe(
      tap((user) => {
        if (user?.email) {
          const role = this.getUserRole(user.email);

          // Restrict access on staging to Dev or God only
          if (
            environment.restrictToDevOrGod &&
            !(
              role === 'dev' ||
              role === 'god' ||
              role === 'admin' ||
              role === 'pm' ||
              role === 'el' ||
              role === 'ic'
            )
          ) {
            this.auth.signOut();
            alert('Access to the staging environment is restricted.');
          }
        }
      }),
      // Map user to role
      (source$) =>
        new Observable<UserRole | null>((subscriber) => {
          return source$.subscribe({
            next: (user) => subscriber.next(this.getUserRole(user?.email)),
            error: (err) => subscriber.error(err),
            complete: () => subscriber.complete(),
          });
        })
    );
  }

  async signInWithGoogle(): Promise<User | null> {
    const provider = new GoogleAuthProvider();
    // Only set the hosted domain
    provider.setCustomParameters({
      hd: 'cannonballcreative.agency',
    });
    // Add scopes we need
    provider.addScope('email');
    provider.addScope('profile');

    // Configure auth settings
    this.auth.useDeviceLanguage();
    this.auth.settings.appVerificationDisabledForTesting = false;

    try {
      console.log('Starting Google sign-in with popup...');
      // Use popup for all environments to avoid storage partitioning issues
      const result = await this.ngZone.runOutsideAngular(() =>
        signInWithPopup(this.auth, provider)
      );

      const user = result.user;
      const email = user?.email;

      console.log('Sign-in successful, got user:', {
        email: email,
        displayName: user?.displayName,
        photoURL: user?.photoURL,
      });

      // Check if user has a valid role (uses same logic as getUserRole)
      const userRole = this.getUserRole(email);
      if (!email || !userRole) {
        console.error('Email not authorized:', email);
        this.authError = 'Sorry, you are not authorized to access this portal.';
        await this.signOut();
        throw new Error(this.authError);
      }

      console.log('Email verified, user authenticated');
      return user;
    } catch (error: any) {
      console.error('Error signing in with Google:', error);

      // Handle specific popup errors gracefully
      if (error.code === 'auth/popup-closed-by-user') {
        console.log('Sign-in popup was closed by the user');
        this.authError = 'Sign-in cancelled';
      } else if (error.code === 'auth/cancelled-popup-request') {
        console.log('Previous sign-in popup was still open');
        this.authError = 'Sign-in already in progress';
      } else {
        this.authError = error.message || 'An error occurred during sign-in';
      }

      throw error;
    }
  }

  async signOut() {
    try {
      await signOut(this.auth);
      this.ngZone.run(() => {
        this.router.navigate(['/']);
      });
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }
}
